import React, { useState, useEffect, useCallback, Component } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Avatar,
  Chip,
  IconButton,
  Divider,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem as SelectMenuItem,
  Radio,
  RadioGroup,
  FormControlLabel,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import EditIcon from '@mui/icons-material/Edit';
import PersonIcon from '@mui/icons-material/Person';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import FavoriteIcon from '@mui/icons-material/Favorite';
import WcIcon from '@mui/icons-material/Wc';
import CakeIcon from '@mui/icons-material/Cake';
import HomeIcon from '@mui/icons-material/Home';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import NotesIcon from '@mui/icons-material/Notes';
import FlagIcon from '@mui/icons-material/Flag';
import KitchenIcon from '@mui/icons-material/Kitchen';
import PeopleIcon from '@mui/icons-material/People';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import TimelineIcon from '@mui/icons-material/Timeline';
import { usersApi, templateApi } from '../services/api';
import RecipeDetailModal from '../components/RecipeDetailModal';

// Safely render a value that might be an object
const safeString = (val) => {
  if (val == null) return '';
  if (typeof val === 'string') return val;
  if (typeof val === 'number' || typeof val === 'boolean') return String(val);
  if (typeof val === 'object') {
    if (val.text) return String(val.text);
    if (val.message) return String(val.message);
    if (val.body) return String(val.body);
    try { return JSON.stringify(val); } catch { return '[Object]'; }
  }
  return String(val);
};

// Error Boundary to prevent child crashes from blanking the whole page
class ProfileErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error('UserProfileCard render error:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <Card sx={{ p: 2 }}>
          <Alert severity="warning">
            Could not render user profile. Some data may be in an unexpected format.
          </Alert>
        </Card>
      );
    }
    return this.props.children;
  }
}

// User Profile Card Component (same as ThreadDetail.js)
const UserProfileCard = ({ userProfile, complexCaseReasons, onRecipeClick }) => {
  if (!userProfile) return null;

  const InfoItem = ({ icon, label, value }) => (
    <Box display="flex" alignItems="flex-start" gap={1} py={0.75}>
      {icon}
      <Box>
        <Typography variant="caption" color="text.secondary" display="block">
          {label}
        </Typography>
        <Typography variant="body2" fontWeight={500}>
          {safeString(value) || 'Not specified'}
        </Typography>
      </Box>
    </Box>
  );

  const ChipList = ({ items, color = 'default' }) => (
    <Box display="flex" flexWrap="wrap" gap={0.5}>
      {items && items.length > 0 ? (
        items.map((item, idx) => {
          const label = typeof item === 'string' ? item.replace(/_/g, ' ') : (item?.name || item?.label || String(item));
          return (
            <Chip
              key={idx}
              label={label}
              size="small"
              color={color}
              variant="outlined"
            />
          );
        })
      ) : (
        <Typography variant="body2" color="text.secondary">
          None
        </Typography>
      )}
    </Box>
  );

  const SectionHeader = ({ icon, label }) => (
    <Box display="flex" alignItems="center" gap={1} mb={1}>
      {icon}
      <Typography variant="caption" fontWeight={600} textTransform="uppercase">
        {label}
      </Typography>
    </Box>
  );

  const hasAdditionalInfo = userProfile.additional_preferences || userProfile.additional_notes || userProfile.specific_meal_plan;

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        {/* Header */}
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
            <PersonIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight={600}>
              {safeString(userProfile.first_name) || 'User'}
            </Typography>
            {userProfile.nutrition_profile && (
              <Typography variant="caption" color="text.secondary">
                {typeof userProfile.nutrition_profile === 'string' ? userProfile.nutrition_profile.replace(/_/g, ' ') : safeString(userProfile.nutrition_profile)}
              </Typography>
            )}
          </Box>
          {userProfile.is_complex_case && (
            <Chip label="Complex Case" size="small" color="warning" icon={<FlagIcon />} sx={{ ml: 'auto' }} />
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Complex Case Alert */}
        {complexCaseReasons && complexCaseReasons.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Complex Case Reasons:
            </Typography>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {complexCaseReasons.map((reason, idx) => (
                <li key={idx}>
                  <Typography variant="body2">{safeString(reason)}</Typography>
                </li>
              ))}
            </ul>
          </Alert>
        )}

        {/* Demographics */}
        <InfoItem
          icon={<WcIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
          label="Gender"
          value={userProfile.gender}
        />
        {userProfile.age && (
          <InfoItem
            icon={<CakeIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
            label="Age"
            value={userProfile.age}
          />
        )}
        <InfoItem
          icon={<CakeIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
          label="Age Group"
          value={typeof userProfile.age_group === 'string' ? userProfile.age_group.replace(/_/g, '-') : safeString(userProfile.age_group)}
        />
        {userProfile.menstruation_status && (
          <InfoItem
            icon={<FavoriteIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
            label="Menstruation Status"
            value={safeString(userProfile.menstruation_status_display) || (typeof userProfile.menstruation_status === 'string' ? userProfile.menstruation_status.replace(/_/g, ' ') : safeString(userProfile.menstruation_status))}
          />
        )}

        <Divider sx={{ my: 2 }} />

        {/* Health Goals */}
        {userProfile.health_goal_display && (
          <Box mb={2}>
            <SectionHeader icon={<FlagIcon sx={{ fontSize: 16, color: 'primary.main' }} />} label="Health Goal" />
            <Typography variant="body2" fontWeight={500}>
              {safeString(userProfile.health_goal_display)}
            </Typography>
          </Box>
        )}

        {/* Health Priorities */}
        <Box mb={2}>
          <SectionHeader icon={<FavoriteIcon sx={{ fontSize: 16, color: 'info.main' }} />} label="Health Priorities" />
          {userProfile.health_priorities_display ? (
            <Typography variant="body2">{safeString(userProfile.health_priorities_display)}</Typography>
          ) : (
            <ChipList items={userProfile.health_priorities} color="info" />
          )}
        </Box>

        {/* Medical Conditions */}
        <Box mb={2}>
          <SectionHeader icon={<LocalHospitalIcon sx={{ fontSize: 16, color: 'error.main' }} />} label="Medical Conditions" />
          {userProfile.medical_conditions_display ? (
            <Typography variant="body2">{safeString(userProfile.medical_conditions_display)}</Typography>
          ) : (
            <ChipList items={userProfile.medical_conditions} color="error" />
          )}
        </Box>

        {/* Allergies */}
        <Box mb={2}>
          <SectionHeader icon={<WarningAmberIcon sx={{ fontSize: 16, color: 'warning.main' }} />} label="Allergies" />
          <ChipList items={userProfile.allergies} color="warning" />
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Dietary Info */}
        <Box mb={2}>
          <SectionHeader icon={<RestaurantIcon sx={{ fontSize: 16, color: 'success.main' }} />} label="Diet Preferences" />
          <Typography variant="body2">
            {safeString(userProfile.diet_preferences) || 'Not specified'}
          </Typography>
        </Box>

        {userProfile.meal_preference && (
          <InfoItem
            icon={<RestaurantIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
            label="Meal Preference"
            value={typeof userProfile.meal_preference === 'string' ? userProfile.meal_preference.replace(/_/g, ' ') : safeString(userProfile.meal_preference)}
          />
        )}

        {userProfile.leftover_preference && (
          <InfoItem
            icon={<KitchenIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
            label="Leftover Preference"
            value={typeof userProfile.leftover_preference === 'string' ? userProfile.leftover_preference.replace(/_/g, ' ') : safeString(userProfile.leftover_preference)}
          />
        )}

        {/* Household Context */}
        {(userProfile.people_count || userProfile.monthly_grocery_budget) && (
          <>
            <Divider sx={{ my: 2 }} />
            <SectionHeader icon={<HomeIcon sx={{ fontSize: 16, color: 'secondary.main' }} />} label="Household" />
            {userProfile.people_count && (
              <InfoItem
                icon={<PeopleIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
                label="Household Size"
                value={`${userProfile.people_count} ${userProfile.people_count === 1 ? 'person' : 'people'}`}
              />
            )}
            {userProfile.monthly_grocery_budget && (
              <InfoItem
                icon={<AttachMoneyIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
                label="Monthly Budget"
                value={userProfile.monthly_grocery_budget}
              />
            )}
          </>
        )}

        {/* Additional Info */}
        {hasAdditionalInfo && (
          <>
            <Divider sx={{ my: 2 }} />
            <SectionHeader icon={<NotesIcon sx={{ fontSize: 16, color: 'text.secondary' }} />} label="Additional Info" />
            {userProfile.additional_preferences && (
              <InfoItem
                icon={<NotesIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
                label="Additional Preferences"
                value={userProfile.additional_preferences}
              />
            )}
            {userProfile.additional_notes && (
              <InfoItem
                icon={<NotesIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
                label="Additional Notes"
                value={userProfile.additional_notes}
              />
            )}
            {userProfile.specific_meal_plan && (
              <InfoItem
                icon={<RestaurantIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
                label="Specific Meal Plan"
                value={userProfile.specific_meal_plan}
              />
            )}
          </>
        )}

        {/* Current Meal Plan Recipes */}
        {Array.isArray(userProfile.current_meal_plan_recipes) && userProfile.current_meal_plan_recipes.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <SectionHeader icon={<MenuBookIcon sx={{ fontSize: 16, color: 'primary.main' }} />} label="Current Meal Plan" />
            <Box sx={{ m: 0 }}>
              {userProfile.current_meal_plan_recipes.map((recipe, idx) => {
                const isObject = typeof recipe === 'object' && recipe !== null;
                const name = isObject ? safeString(recipe.name) : safeString(recipe);
                const recipeId = isObject ? recipe.id : null;

                if (!name) return null;

                return (
                  <Box
                    key={recipeId || idx}
                    sx={{
                      p: 1,
                      borderRadius: 1,
                      mb: 0.5,
                      ...(recipeId && onRecipeClick ? {
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.hover' },
                      } : {}),
                    }}
                    onClick={recipeId && onRecipeClick ? () => onRecipeClick(recipeId) : undefined}
                  >
                    <Typography
                      variant="body2"
                      fontWeight={500}
                      sx={recipeId && onRecipeClick ? { color: 'primary.main', textDecoration: 'underline', textDecorationStyle: 'dotted' } : {}}
                    >
                      {name}
                    </Typography>
                    {isObject && (
                      <Box display="flex" gap={1} flexWrap="wrap" mt={0.5}>
                        {typeof recipe.calories === 'number' && (
                          <Typography variant="caption" color="text.secondary">{Math.round(recipe.calories)} cal</Typography>
                        )}
                        {typeof recipe.protein === 'number' && (
                          <Typography variant="caption" color="text.secondary">P: {recipe.protein}g</Typography>
                        )}
                        {typeof recipe.carbs === 'number' && (
                          <Typography variant="caption" color="text.secondary">C: {recipe.carbs}g</Typography>
                        )}
                        {typeof recipe.fat === 'number' && (
                          <Typography variant="caption" color="text.secondary">F: {recipe.fat}g</Typography>
                        )}
                        {Array.isArray(recipe.allergens) && recipe.allergens.map((a, i) => (
                          <Chip key={i} label={safeString(a) || 'Unknown'} size="small" color="error" variant="outlined" sx={{ height: 18, fontSize: 10 }} />
                        ))}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};

function CreateCheckin() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // User data state
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Template state
  const [templates, setTemplates] = useState(null);
  const [templatesLoading] = useState(false); // Templates loaded with user data
  const [selectedWeek, setSelectedWeek] = useState(0);
  const [selectedQuestion, setSelectedQuestion] = useState(''); // Single selection with radio
  const [selectedSignOff, setSelectedSignOff] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);

  // Submit state
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);

  // Recipe detail modal state
  const [selectedRecipeId, setSelectedRecipeId] = useState(null);

  // Fetch user data and templates
  const fetchUserData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch full user profile including current_meal_plan_recipes
      const userData = await usersApi.getUser(parseInt(userId));
      const foundUser = userData.user;

      if (!foundUser) {
        setError('User not found');
        setLoading(false);
        return;
      }

      setUser(foundUser);

      // Set user profile from the full user data (includes current_meal_plan_recipes)
      setUserProfile({
        first_name: foundUser.first_name,
        gender: foundUser.gender,
        age: foundUser.age,
        age_group: foundUser.age_group,
        menstruation_status: foundUser.menstruation_status,
        menstruation_status_display: foundUser.menstruation_status_display,
        health_goal: foundUser.health_goal,
        health_goal_display: foundUser.health_goal_display,
        health_priorities: foundUser.health_priorities,
        health_priorities_display: foundUser.health_priorities_display,
        medical_conditions: foundUser.medical_conditions,
        medical_conditions_display: foundUser.medical_conditions_display,
        allergies: foundUser.allergies,
        diet_preferences: foundUser.diet_preferences,
        leftover_preference: foundUser.leftover_preference,
        is_complex_case: foundUser.is_complex_case,
        complex_case_reasons: foundUser.complex_case_reasons,
        current_meal_plan_recipes: foundUser.current_meal_plan_recipes,
      });

      // Fetch templates - try user-specific first, then fall back to general templates
      let templateResult = null;
      let recommendedWeek = 1;

      try {
        // Try user-specific templates endpoint first
        const templateData = await usersApi.getTemplates(userId);

        // Get recommended week
        recommendedWeek = templateData.recommended_week || 1;

        // Parse templates from response
        if (templateData.recommended_templates) {
          templateResult = {
            weekly_checkin_templates: templateData.recommended_templates.weekly_checkin || [],
            sign_off_templates: templateData.recommended_templates.sign_offs || templateData.recommended_templates.sign_off || [],
            milestone_templates: templateData.recommended_templates.milestone || [],
            recommended_week: recommendedWeek,
          };
        } else if (templateData.templates) {
          templateResult = {
            weekly_checkin_templates: templateData.templates.filter(t =>
              t.category === 'WEEKLY_CHECKIN' || t.category?.toUpperCase() === 'WEEKLY_CHECKIN'
            ),
            sign_off_templates: templateData.templates.filter(t =>
              t.category === 'SIGN_OFF' || t.category?.toUpperCase() === 'SIGN_OFF'
            ),
            milestone_templates: templateData.templates.filter(t =>
              t.category === 'MILESTONE' || t.category?.toUpperCase() === 'MILESTONE'
            ),
            recommended_week: recommendedWeek,
          };
        }
      } catch (userTemplateErr) {
        console.warn('User templates endpoint failed, falling back to general templates:', userTemplateErr.message);
      }

      // If no weekly_checkin_templates found, fetch from general templates endpoint
      if (!templateResult?.weekly_checkin_templates?.length) {
        try {
          // Fetch weekly check-in templates
          const weeklyData = await templateApi.getAll('WEEKLY_CHECKIN');
          const weeklyTemplates = weeklyData.templates || weeklyData || [];

          // Fetch sign-off templates
          const signOffData = await templateApi.getAll('SIGN_OFF');
          const signOffTemplates = signOffData.templates || signOffData || [];

          templateResult = {
            weekly_checkin_templates: Array.isArray(weeklyTemplates) ? weeklyTemplates : [],
            sign_off_templates: Array.isArray(signOffTemplates) ? signOffTemplates : [],
            milestone_templates: [],
            recommended_week: recommendedWeek,
          };
        } catch (generalTemplateErr) {
          console.error('Error fetching general templates:', generalTemplateErr);
        }
      }

      // Set templates if we have any
      if (templateResult) {
        setTemplates(templateResult);

        // Set recommended week
        if (templateResult.recommended_week) {
          setSelectedWeek(Math.max(0, templateResult.recommended_week - 1));
        }

        // Pre-select first template of the week
        const weekTemplates = getWeekTemplates(templateResult.weekly_checkin_templates, templateResult.recommended_week || 1);
        if (weekTemplates.length > 0) {
          setSelectedQuestion(weekTemplates[0].id.toString());
        }

        // Pre-select first sign-off
        if (templateResult.sign_off_templates?.length > 0) {
          setSelectedSignOff(templateResult.sign_off_templates[0].id.toString());
        }
      } else {
        console.error('No templates could be loaded');
      }
    } catch (err) {
      setError('Failed to load user data');
      console.error('User fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  const getWeekTemplates = (weeklyTemplates, weekNum) => {
    if (!weeklyTemplates) return [];
    return weeklyTemplates.filter(t => t.week_number === weekNum || t.week === weekNum);
  };

  const renderTemplate = async (templateId) => {
    try {
      const data = await templateApi.render(templateId, { userId: parseInt(userId) });
      return data.rendered_body || data.body || data.content || '';
    } catch (err) {
      console.error('Error rendering template:', err);
      // Fallback: try to get full template
      try {
        const fullTemplate = await templateApi.get(templateId);
        let body = fullTemplate.body || fullTemplate.content || '';
        if (user?.first_name && body) {
          body = body.replace(/\[Name\]/gi, user.first_name);
        }
        return body;
      } catch (getErr) {
        console.error('Error getting full template:', getErr);
        return '';
      }
    }
  };

  const buildMessage = async () => {
    if (!templates) return '';
    let parts = [];

    // Add selected question template
    if (selectedQuestion) {
      const rendered = await renderTemplate(parseInt(selectedQuestion));
      if (rendered) parts.push(rendered);
    }

    // Add sign-off
    if (selectedSignOff) {
      const signOffRendered = await renderTemplate(parseInt(selectedSignOff));
      if (signOffRendered) parts.push('\n' + signOffRendered);
    }

    return parts.join('\n\n');
  };

  const handleEditMode = async () => {
    const message = await buildMessage();
    setCustomMessage(message);
    setIsEditMode(true);
  };

  const handleWeekChange = (_, newWeek) => {
    setSelectedWeek(newWeek);
    // Pre-select first template of the new week
    const weekTemplates = getWeekTemplates(templates?.weekly_checkin_templates, newWeek + 1);
    if (weekTemplates.length > 0) {
      setSelectedQuestion(weekTemplates[0].id.toString());
    } else {
      setSelectedQuestion('');
    }
  };

  const handleSendCheckin = async () => {
    setSending(true);
    setSendError(null);

    try {
      let messageToSend = '';

      if (isEditMode && customMessage.trim()) {
        messageToSend = customMessage.trim();
      } else if (selectedQuestion) {
        messageToSend = await buildMessage();
      } else if (customMessage.trim()) {
        messageToSend = customMessage.trim();
      }

      if (!messageToSend) {
        setSendError('Please select a template or write a message.');
        setSending(false);
        return;
      }

      const result = await usersApi.createCheckin(parseInt(userId), messageToSend);

      // Navigate to the new thread
      if (result.thread?.reference_id) {
        navigate(`/thread/${result.thread.reference_id}`);
      } else {
        navigate('/users');
      }
    } catch (err) {
      setSendError(err.response?.data?.error || 'Failed to create check-in. Please try again.');
      console.error('Create check-in error:', err);
    } finally {
      setSending(false);
    }
  };

  const currentWeekTemplates = templates?.weekly_checkin_templates
    ? getWeekTemplates(templates.weekly_checkin_templates, selectedWeek + 1)
    : [];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/users')} sx={{ mb: 2 }}>
          Back to Users
        </Button>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <IconButton onClick={() => navigate('/users')}>
          <ArrowBackIcon />
        </IconButton>
        <Box flex={1}>
          <Typography variant="h5" fontWeight={700} color="secondary.dark">
            Create Weekly Check-in
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {user?.first_name}
            {templates?.recommended_week && ` • Recommended: Week ${templates.recommended_week}`}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={<TimelineIcon />}
          onClick={() => navigate(`/users/${userId}/timeline`)}
          sx={{ whiteSpace: 'nowrap' }}
        >
          Client History
        </Button>
      </Box>

      {/* Content */}
      <Box display="flex" gap={3} flexDirection={isMobile ? 'column' : 'row'}>
        {/* User Profile - Sidebar on desktop, top on mobile */}
        <Box sx={{ width: isMobile ? '100%' : 320, flexShrink: 0 }}>
          <ProfileErrorBoundary>
            <UserProfileCard
              userProfile={userProfile}
              complexCaseReasons={userProfile?.complex_case_reasons}
              onRecipeClick={(recipeId) => setSelectedRecipeId(recipeId)}
            />
          </ProfileErrorBoundary>
        </Box>

        {/* Check-in Composer */}
        <Box flex={1} display="flex" flexDirection="column" minWidth={0}>
          <Card>
            <CardContent>
              {sendError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {sendError}
                </Alert>
              )}

              {templatesLoading ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress />
                </Box>
              ) : isEditMode ? (
                <Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      Edit Message
                    </Typography>
                    <Button size="small" onClick={() => setIsEditMode(false)}>
                      Back to Templates
                    </Button>
                  </Box>
                  <TextField
                    fullWidth
                    value={customMessage}
                    onChange={(e) => setCustomMessage(e.target.value)}
                    multiline
                    rows={16}
                    placeholder="Write your check-in message..."
                  />
                </Box>
              ) : templates ? (
                <Box>
                  {/* Week Selection Tabs */}
                  <Typography variant="subtitle2" fontWeight={600} mb={1}>
                    Select Week
                  </Typography>
                  <Tabs
                    value={selectedWeek}
                    onChange={handleWeekChange}
                    sx={{ mb: 3 }}
                    variant="scrollable"
                    scrollButtons="auto"
                  >
                    <Tab label="Week 1 - Meal Plan" />
                    <Tab label="Week 2 - Challenges" />
                    <Tab label="Week 3 - Shopping" />
                    <Tab label="Week 4 - Progress" />
                  </Tabs>

                  {/* Question Selection - Radio Buttons (single selection) */}
                  <Typography variant="subtitle2" fontWeight={600} mb={1}>
                    Select Question
                  </Typography>
                  <Card variant="outlined" sx={{ mb: 3, maxHeight: 300, overflowY: 'auto' }}>
                    {currentWeekTemplates.length > 0 ? (
                      <RadioGroup
                        value={selectedQuestion}
                        onChange={(e) => setSelectedQuestion(e.target.value)}
                      >
                        {currentWeekTemplates.map((template) => (
                          <Box key={template.id} sx={{ p: 2, borderBottom: '1px solid #eee' }}>
                            <FormControlLabel
                              value={template.id.toString()}
                              control={<Radio />}
                              label={
                                <Box>
                                  <Typography variant="body2" fontWeight={500}>
                                    {template.name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {(template.body || template.content)?.substring(0, 150)}...
                                  </Typography>
                                </Box>
                              }
                              sx={{ alignItems: 'flex-start', m: 0, width: '100%' }}
                            />
                          </Box>
                        ))}
                      </RadioGroup>
                    ) : (
                      <Box p={3} textAlign="center">
                        <Typography color="text.secondary">
                          No templates available for this week
                        </Typography>
                      </Box>
                    )}
                  </Card>

                  {/* Sign-off Selection */}
                  {templates.sign_off_templates?.length > 0 && (
                    <Box mb={3}>
                      <Typography variant="subtitle2" fontWeight={600} mb={1}>
                        Sign-off
                      </Typography>
                      <FormControl fullWidth size="small">
                        <InputLabel>Select sign-off</InputLabel>
                        <Select
                          value={selectedSignOff}
                          onChange={(e) => setSelectedSignOff(e.target.value)}
                          label="Select sign-off"
                        >
                          <SelectMenuItem value="">None</SelectMenuItem>
                          {templates.sign_off_templates.map((signOff) => (
                            <SelectMenuItem key={signOff.id} value={signOff.id.toString()}>
                              {signOff.name}
                            </SelectMenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  )}

                  <Divider sx={{ my: 2 }} />

                  {/* Preview & Edit Button */}
                  <Button
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={handleEditMode}
                    disabled={!selectedQuestion}
                  >
                    Preview & Edit Message
                  </Button>
                </Box>
              ) : (
                <Box>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    Templates could not be loaded. You can write a custom message.
                  </Typography>
                  <TextField
                    fullWidth
                    value={customMessage}
                    onChange={(e) => setCustomMessage(e.target.value)}
                    multiline
                    rows={12}
                    placeholder="Write your check-in message..."
                  />
                </Box>
              )}

              {/* Action Buttons */}
              <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/users')}
                  disabled={sending}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  endIcon={sending ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                  onClick={handleSendCheckin}
                  disabled={
                    sending ||
                    (!isEditMode && !customMessage.trim() && !selectedQuestion)
                  }
                >
                  {sending ? 'Sending...' : 'Send Check-in'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Recipe Detail Modal */}
      <RecipeDetailModal
        open={!!selectedRecipeId}
        onClose={() => setSelectedRecipeId(null)}
        recipeId={selectedRecipeId}
      />
    </Box>
  );
}

export default CreateCheckin;
